import { getAuthHeaders } from './components/Common';
import { getFcmToken } from './firebaseMessaging';

const PUSH_TOKEN_STORAGE_KEY = 'saferPushToken';
const DEVICE_ID_STORAGE_KEY = 'saferDeviceId';

export function getBrowserDeviceName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Edg/')) {
    return 'Edge';
  }
  if (ua.includes('Chrome/')) {
    return 'Chrome';
  }
  if (ua.includes('Firefox/')) {
    return 'Firefox';
  }
  if (ua.includes('Safari/')) {
    return 'Safari';
  }
  return 'Web browser';
}

function getOrCreateDeviceId(): string {
  try {
    const existing = sessionStorage.getItem(DEVICE_ID_STORAGE_KEY);
    if (existing) {
      return existing;
    }
    const deviceId = crypto.randomUUID();
    sessionStorage.setItem(DEVICE_ID_STORAGE_KEY, deviceId);
    return deviceId;
  } catch {
    // private mode / blocked storage — ephemeral id for this call only
    return crypto.randomUUID();
  }
}

function loadStoredDeviceId(): string | null {
  try {
    return sessionStorage.getItem(DEVICE_ID_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearStoredDeviceId(): void {
  try {
    sessionStorage.removeItem(DEVICE_ID_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function storePushToken(pushToken: string): void {
  try {
    localStorage.setItem(PUSH_TOKEN_STORAGE_KEY, pushToken);
  } catch {
    // ignore quota / private mode
  }
}

function loadStoredPushToken(): string | null {
  try {
    return localStorage.getItem(PUSH_TOKEN_STORAGE_KEY);
  } catch {
    return null;
  }
}

function clearStoredPushToken(): void {
  try {
    localStorage.removeItem(PUSH_TOKEN_STORAGE_KEY);
  } catch {
    // ignore
  }
}

export async function requestPushPermission(): Promise<NotificationPermission> {
  if (!('Notification' in window)) {
    console.warn('Push: Notification API not available in this browser');
    return 'denied';
  }
  if (Notification.permission === 'granted') {
    return 'granted';
  }
  if (Notification.permission === 'denied') {
    console.warn(
      'Push: notifications blocked for this site — reset in browser site settings',
    );
    return 'denied';
  }
  // Must run close to a user click; Chrome may skip the prompt after long async chains.
  return Notification.requestPermission();
}

export async function registerPushDevice(apiHost: string): Promise<void> {
  if (!('Notification' in window) || !('serviceWorker' in navigator)) {
    console.warn('Push: notifications or service workers not supported');
    return;
  }
  const permission = await requestPushPermission();
  if (permission !== 'granted') {
    console.warn(`Push: permission not granted (${permission})`);
    return;
  }
  const pushToken = await getFcmToken();
  if (!pushToken) {
    return;
  }
  const deviceId = getOrCreateDeviceId();
  const response = await fetch(`${apiHost}/v1/user/devices`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      device_id: deviceId,
      push_token: pushToken,
      device_name: getBrowserDeviceName(),
      device_type: 'web',
    }),
  });
  if (!response.ok) {
    throw new Error(`Device registration failed: HTTP ${response.status}`);
  }
  storePushToken(pushToken);
}

export async function unregisterPushDevice(apiHost: string): Promise<void> {
  const deviceId = loadStoredDeviceId();
  if (!deviceId) {
    clearStoredPushToken();
    return;
  }
  try {
    await fetch(`${apiHost}/v1/user/devices`, {
      method: 'DELETE',
      headers: {
        ...getAuthHeaders(),
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ device_id: deviceId }),
    });
  } finally {
    clearStoredPushToken();
    clearStoredDeviceId();
  }
}
